/**
 * MC Fleet Activity — the `/mc/feed` page module (FEED-01..04, 232-UI-SPEC).
 *
 * Initializes on `/mc/feed` only (guards on `#mc-feed-stream` presence — feed.latte's own
 * unconditional first-paint marker, mirroring `initAnalytics()`/`initLeaderboard()`'s own
 * `#mc-analytics-chart`/`#mc-metric-select` guard idiom). Fetches every fleet-wide signal
 * (`?do=getFeedStream/getKd/getRarity/getFirsts/getChatVolume/getChatHours/getChatWordRank`) and
 * renders each into the DOM markers Plan 05's `feed.latte` laid down.
 *
 * ⚠ EVERY payload-derived string (narrative, advancement title, player name) is written via
 * `textContent`/`createElement` — NEVER `innerHTML` — enforced by literal grep (T-232-03).
 *
 * ⚠ `payload.content` (chat message text) is never requested or read anywhere in this file —
 * the three Chat Activity fetches return counts only (T-232-02).
 *
 * ⚠ Both rules above are enforced by LITERAL grep, so neither is spelled out with its forbidden
 * character/string here — same discipline as `leaderboard.ts`'s own header comment.
 *
 * D-05: the stream range selector (`#mc-feed-stream-range`) and the chat volume range selector
 * (`#mc-feed-chat-range`) are two INDEPENDENT `.mc-range-btn` groups. Every binding below is
 * scoped to its own container id — a bare global `.mc-range-btn` querySelectorAll would double-
 * bind both groups to the same handler (the documented shared-component double-bind trap,
 * `feedback_shared_component_every_page_init_double_bind`).
 *
 * D-05: the type filter (`#mc-feed-type-filter`) is a SERVER-SIDE `type` signal param, never a
 * client-side re-filter of already-fetched rows — a client-side filter interacting with
 * offset-based "Load more" pagination would silently return sparse pages.
 *
 * Pitfall 6: the word-rank table (`data-table="mc-feed-word-rank"`) is a CLIENT-SIDE
 * `datatables.net-bs5` instance — no server-side-paging option and no remote-data-source option,
 * ever (mirrors leaderboard.ts's own discipline of not spelling out a forbidden option verbatim
 * in a comment a grep might match).
 */

import * as Highcharts from 'highcharts';
import DataTable from 'datatables.net-bs5';
import { getChartTheme } from '../utils/chartTheme';
import { localizeInstant } from '../utils/instants';
import { POLL_INTERVAL } from '../utils/poll';
import { prefersReducedMotion } from '../util';
import {
    FEED_PAGE_LIMIT,
    PILL_QUEUE_CAP,
    tailSinceParam,
    selectUnseenRows,
    recordSeenIds,
    advanceWatermark,
    compensatedOffset,
    shouldResetToPage0,
    arrivalLabel,
    aggregateRefetchTargets,
} from '../utils/feedTail';
import { laneSegmentsForRows, MIN_LANE_COUNT, type LaneSegment } from '../utils/feedArcs';
import { appendNarrativeSegments, type NarrativeSegment } from './coordPill';
// Phase 272 (SURF-02, D-09/D-11): the shared death-forensics expand panel — imperative Bootstrap
// Collapse instantiation follows tracking.ts's own precedent (`new Collapse(el, { toggle: false
// })`); Bootstrap is NOT on `window` in this build.
import { Collapse } from 'bootstrap';
import { renderInto } from './forensicsPanel';
// Phase 276 Plan 09 (D-09, QNAME-04, T-276-34): the SAME idempotent Popover-binding pass
// `initQuestDetail()` runs once at page load for server-rendered triggers — this file never
// constructs a `Popover` itself (that guard is owned by `questDetail.ts` alone, per this plan's
// own acceptance criteria), it only calls the already-idempotent bind function on every row this
// module inserts into the DOM after that initial pass has already run.
import { attachQuestDetailTriggers } from './questDetail';

// ---------------------------------------------------------------------------
// Payload types — App\Models\McFeedModel's fleet-wide signal shapes
// ---------------------------------------------------------------------------

interface StreamRow {
    id: string;
    event_type: string;
    badge: string;
    narrative: string;
    bluemap_href: string | null;
    server_key: string;
    server_icon: string | null;
    server_name: string;
    player_uuid: string;
    player_name: string;
    occurred_at_ms: number;
    // Phase 254 D-07: the ordered text/coord segment array plan 254-03 emits. Optional because a
    // row can arrive from a pre-254 backend during a deploy window — `narrative` above is the
    // fallback this plan's client consumes when this field is absent or not an array.
    narrative_segments?: NarrativeSegment[];
    // Phase 254 D-05/D-08: the row's own dimension, so a feed mixing Overworld/Nether/End deaths
    // tints each row's pill by that row's own dimension. Optional for the same deploy-window reason.
    dimension?: string | null;
    // Phase 272 (D-11, SURF-02): true only for a death row with a recoverable grave record
    // (PC-3: ~61% of deaths have none). Optional for the same deploy-window reason
    // `narrative_segments`/`dimension` above already document — a row arriving from a pre-272
    // backend degrades to "no chevron", never a crash. A strict `=== true` check at the one call
    // site below is what keeps `undefined`/`null`/`0`/`'0'` from ever rendering a chevron.
    has_forensics?: boolean;
}

interface StreamPayload {
    has_data: boolean;
    empty_kind: string | null;
    next_offset: number | null;
    rows: StreamRow[];
}

/**
 * The tail signal's response envelope (Phase 243, D-05/D-06/D-08) — deliberately NOT
 * `StreamPayload`'s shape. The tail never renders an empty state (D-14 falls back to a normal
 * `fetchStream(false)` page-0 call when the client holds no baseline) and it has no `OFFSET`, so
 * this interface omits `has_data`, `empty_kind` and `next_offset` on purpose rather than carrying
 * three fields that would always be meaningless here. Matches
 * `McFeedModel::getActivityStreamTail()`'s return shape exactly. `FEED_PAGE_LIMIT` documents the
 * page size `overflowed` is measured against server-side; it is not itself sent on the wire.
 */
interface TailPayload {
    rows: StreamRow[];
    overflowed: boolean;
}

interface KdRow {
    rank: number;
    player_uuid: string;
    player_name: string;
    kills: number;
    deaths: number;
    kd: number | null;
}

interface KdPayload {
    has_data: boolean;
    label: string;
    rows: KdRow[];
}

interface RarityRow {
    rank: number;
    advancement_id: string;
    title: string;
    earned_by: number;
}

interface RarityPayload {
    has_data: boolean;
    m_players: number;
    rows: RarityRow[];
}

interface FirstsRow {
    advancement_id: string;
    title: string;
    player_uuid: string;
    player_name: string;
    occurred_at_ms: number;
}

interface FirstsPayload {
    has_data: boolean;
    rows: FirstsRow[];
}

interface ChatVolumePayload {
    has_data: boolean;
    points: [number, number][];
}

interface ChatHoursBar {
    hour: number;
    count: number;
}

interface ChatHoursPayload {
    has_data: boolean;
    bars: ChatHoursBar[];
}

interface WordRankRow {
    player_uuid: string;
    player_name: string;
    messages: number;
    total_words: number;
    avg_words: number;
}

interface WordRankPayload {
    has_data: boolean;
    rows: WordRankRow[];
}

// ---------------------------------------------------------------------------
// Badge vocabulary (§7.3/§7.5b) — a fixed allow-list lookup, never a raw class-name
// interpolation from server data (mirrors mc.ts's T-231-10 presence-badge discipline).
// ---------------------------------------------------------------------------

// Phase 270 (D-21) — 'quest-started'/'quest-completed' are the client mirror of
// McFeedModel::buildActivityRow()'s badge vocabulary. A badge added on one side without the
// other renders as bare, unstyled text equal to the raw badge key (badgeEl()'s fallback below).
const BADGE_CLASS: Record<string, string> = {
    death: 'mc-feed-badge--death',
    'kill-pvp': 'mc-feed-badge--kill-pvp',
    'kill-boss': 'mc-feed-badge--kill-boss',
    'session-join': 'mc-feed-badge--session-join',
    'session-quit': 'mc-feed-badge--session-quit',
    'quest-started': 'mc-feed-badge--quest-started',
    'quest-completed': 'mc-feed-badge--quest-completed',
    first: 'mc-feed-badge--first',
};

const BADGE_LABEL: Record<string, string> = {
    death: 'Death',
    'kill-pvp': 'PvP Kill',
    'kill-boss': 'Boss Kill',
    'session-join': 'Joined',
    'session-quit': 'Left',
    'quest-started': 'Started',
    'quest-completed': 'Completed',
    first: 'First',
};

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

/**
 * D-06/Phase 249 (D-09/D-10/D-11): the checked server keys, or `[]` — the D-10 "all servers"
 * state. Initialised from the picker's own `selectedOptions`, mirroring leaderboard.ts's
 * identical state shape (Plan 249-08). Every request re-validates `server[]` server-side; the
 * client-side option-existence check in `initFeed()` below is a UX affordance only.
 */
let currentServers: string[] = [];
let currentStreamRange = '24h';
let currentType = 'all';
let streamOffset = 0;
let streamNextOffset: number | null = null;

let currentChatRange = '24h';

// Request-ordering guards (CR-01) — one monotonic counter per fetch function, mirroring
// profile.ts's requestSeq/sessionRequestSeq pattern: increment before issuing, discard the
// response after await if a newer request for that same section has since been issued. Without
// this, whichever HTTP response happened to arrive last would win the render, not whichever was
// requested last (e.g. a rapid server-picker change could leave a section permanently showing
// the previous server's data).
let streamSeq = 0;
let kdSeq = 0;
let raritySeq = 0;
let firstsSeq = 0;
let chatVolumeSeq = 0;
let chatHoursSeq = 0;
let wordRankSeq = 0;

let volumeChart: Highcharts.Chart | null = null;
let hoursChart: Highcharts.Chart | null = null;
let wordRankTable: any = null;

/** Idempotency guard — mirrors leaderboard.ts's own `listenersBound` discipline. */
let listenersBound = false;

// ---------------------------------------------------------------------------
// Phase 243 — live tail state (D-05, D-06, D-07, D-08, D-09, D-13, D-14)
// ---------------------------------------------------------------------------

/** The poll-tick timer handle — mirrors mc.ts's own `detailPollTimer` (mc.ts:644). */
let feedPollTimer: ReturnType<typeof setTimeout> | null = null;

/** D-05's tail watermark: the newest `occurred_at_ms` this client has ever held. `null` means
 *  the client holds zero rows, which is exactly D-14's page-0-fallback condition. */
let newestKnownOccurredAtMs: number | null = null;

/** D-06's dedup set, keyed by `mc_events.id`. Capped and oldest-evicted by `recordSeenIds()`. */
const renderedEventIds = new Set<string>();

/**
 * Phase 250 Plan 04 (D-01..D-07, D-13): mirrors `#mc-feed-stream`'s children exactly, in the
 * same order, pairing each rendered row's typed data with its own element. This is what lets
 * the arc-recompute function get both the row data `laneSegmentsForRows()` needs and the DOM
 * element to write segments into, in one pass, with no `dataset` serialize/deserialize round
 * trip.
 *
 * Takes NO cap of its own: `shouldResetToPage0()` already fires at `STREAM_ROW_CAP`
 * (`utils/feedTail.ts`) before the DOM can exceed it, and a second, independently-tuned cap here
 * would let this array and the DOM silently diverge.
 */
let renderedRows: { row: StreamRow; el: HTMLElement }[] = [];

/** D-07: the count of rows prepended since the last page-0 render. Added to every subsequent
 *  Load-more offset to correct for the server's positional window shifting down by that amount. */
let offsetCompensation = 0;

/** D-09: rows that arrived while the viewer was scrolled away from the stream head. Plan 243-05
 *  renders the pill that surfaces this queue; this plan only accumulates it. */
let queuedRows: StreamRow[] = [];

/** D-09/D-11: the announced arrival count. Can exceed `queuedRows.length` once the queue has
 *  been emptied for passing `PILL_QUEUE_CAP` (see `queueResetPending` below) — the announced
 *  number stays honest even after the underlying rows are discarded. */
let queuedCount = 0;

/** Set when the queue passed `PILL_QUEUE_CAP` while the viewer was away from the head. Routes
 *  the eventual flush into the page-0 recovery path instead of prepending a partial batch. */
let queueResetPending = false;

/**
 * Resets every piece of Phase 243 tail state. Called as the FIRST statement of `refetchAll()`:
 * any change to server, range or type replaces the loaded row set entirely, so the tail
 * baseline, the dedup set, the D-07 compensation counter and the D-09 pill queue must all reset
 * together — otherwise a tail fetch still in flight (or issued right after) could prepend the
 * previous filter's rows onto the new filter's list.
 */
function resetTailState(): void {
    newestKnownOccurredAtMs = null;
    renderedEventIds.clear();
    offsetCompensation = 0;
    queuedRows = [];
    queuedCount = 0;
    queueResetPending = false;
    updateArrivalPill(); // a filter or server change dismisses a stale pill along with the reset
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function initFeed(): void {
    const streamEl = document.getElementById('mc-feed-stream');
    if (!streamEl) return; // Only runs on /mc/feed

    if (listenersBound) return;
    listenersBound = true;

    const serverSelect = document.getElementById('mc-server-select') as HTMLSelectElement | null;
    if (serverSelect) {
        // D-06/Phase 249 (D-09/D-10/D-11): multi-value deep-link pre-select, mirroring
        // leaderboard.ts's identical read (Plan 249-08). Every `server[]` value is read, plus the
        // legacy singular `server` value so an old bookmark still pre-selects. The attribute
        // selector below stays guarded exactly as before against each untrusted value; a value
        // with no matching <option> is skipped entirely rather than echoed back.
        const params = new URLSearchParams(window.location.search);
        const deepLinkServers = params.getAll('server[]');
        const legacyServer = params.get('server');
        if (legacyServer) deepLinkServers.push(legacyServer);
        for (const deepLinkServer of deepLinkServers) {
            const option = serverSelect.querySelector(`option[value="${CSS.escape(deepLinkServer)}"]`);
            if (option) (option as HTMLOptionElement).selected = true;
        }

        currentServers = Array.from(serverSelect.selectedOptions).map((o) => o.value);
        serverSelect.addEventListener('change', () => {
            currentServers = Array.from(serverSelect.selectedOptions).map((o) => o.value);
            syncUrl();
            refetchAll();
        });
    }

    bindStreamRange();
    bindTypeFilter();
    bindLoadMore();
    bindArrivalPill();
    bindChatRange();

    // D-11 auto-flush on scroll-back: the pill must never linger stale once the viewer is back
    // at the head, whether they got there by clicking the pill or by scrolling on their own --
    // D-09's "auto-prepend when at the head" stays the single governing rule either way. Returns
    // immediately unless a queue exists, so this listener is inert on the overwhelmingly common
    // scroll path. One passive listener, registered inside this same idempotency guard.
    window.addEventListener('scroll', () => {
        if (queuedCount === 0) return;
        if (isAtStreamHead()) {
            flushArrivalQueue(false);
        }
    }, { passive: true });

    refetchAll();

    // D-13: poll scaffold, a structural port of initDetail()'s scheduleDetailPoll()/pollDetail()
    // pair (mc.ts:687-704) -- same clearTimeout-then-null discipline, same document.hidden
    // branch, same immediate-fetch-on-return. Registered inside the listenersBound guard above
    // so a defensive re-call to initFeed() can never double-register either the timer or the
    // listener (the double-bind trap this file's own header comment already names).
    schedulePollTick();
    document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
            if (feedPollTimer !== null) {
                clearTimeout(feedPollTimer);
                feedPollTimer = null;
            }
        } else {
            void pollTick();
        }
    });
}

/**
 * D-09 Phase 249: one query-term builder shared by all eight signals below (the stream, the
 * tail, the K/D table, the rarity table, the firsts list, the chat volume, the chat hours and the
 * chat word rank), so multi-server scoping can never drift between them -- eight hand-rolled
 * loops would be eight chances to disagree. Returns '' for the D-10 "all servers" state (an
 * empty `currentServers`), or a leading-ampersand-prefixed `server[]=` term per checked key.
 */
function serverQueryTerms(): string {
    return currentServers
        .map((v) => `&server[]=${encodeURIComponent(v)}`)
        .join('');
}

/**
 * D-11 Phase 249: net-new URL persistence -- this page never wrote its server selection back to
 * the address bar before this plan (RESEARCH.md Pitfall 7; leaderboard.ts's own `syncUrl()`,
 * Plan 249-08, is the pattern mirrored here). Deletes BOTH the legacy scalar `server` key and the
 * bracket `server[]` key before appending, so a stale bookmarked scalar can never survive
 * alongside the new list (T-249-08-T2).
 *
 * The selection rides in the URL and NOWHERE else -- neither browser storage API is used
 * anywhere in this module, and nothing is written server-side. This repo has a documented bug
 * class where persisted dashboard query params let one visitor (or a probe) clobber another's
 * saved state; a read-only feed must not acquire it. The state-write call below is the
 * non-history-appending kind, so a viewer flipping through servers does not have to walk back
 * through every selection to leave the page.
 */
function syncUrl(): void {
    const url = new URL(window.location.href);
    url.searchParams.delete('server');
    url.searchParams.delete('server[]');
    for (const v of currentServers) {
        url.searchParams.append('server[]', v);
    }
    window.history.replaceState(null, '', url.toString());
}

/** A server-picker change refetches EVERY section (§7.1). */
function refetchAll(): void {
    // Phase 243 (Claude's Discretion, 243-CONTEXT.md): refetchAll() is the right seam for the
    // tail-state reset -- any change to server, range or type replaces the loaded set entirely,
    // so the tail baseline, the compensation counter and the pill queue must all reset together
    // or a tail fetch will prepend the previous filter's rows.
    resetTailState();
    streamOffset = 0;
    streamNextOffset = null;
    void fetchStream(false);
    void fetchKd();
    void fetchRarity();
    void fetchFirsts();
    void fetchChatVolume();
    void fetchChatHours();
    void fetchChatWordRank();
}

/** Schedules the next poll tick on the shared cadence -- structurally identical to
 *  `mc.ts`'s own `scheduleDetailPoll()`. */
function schedulePollTick(): void {
    feedPollTimer = setTimeout(() => { void pollTick(); }, POLL_INTERVAL);
}

/**
 * D-05/D-13/D-14/D-16 poll tick. Opens with the SAME clearTimeout-then-null guard
 * `pollDetail()` opens with (mc.ts:711-714), so a visibility-triggered immediate tick can never
 * race the scheduled one. Reschedules unconditionally in `finally` -- a failed tick still
 * retries on the next tick (D-04), so a thrown error must never break the reschedule chain.
 *
 * D-01: this tick NEVER touches `volumeChart`, `hoursChart` or `wordRankTable` -- the same
 * discipline `initDetail()`'s own comment states for `initResourceHistory()`.
 */
async function pollTick(): Promise<void> {
    if (feedPollTimer !== null) {
        clearTimeout(feedPollTimer);
        feedPollTimer = null;
    }

    try {
        const since = tailSinceParam(newestKnownOccurredAtMs);
        if (since === null) {
            // D-14: the client holds zero rows (a dark fleet, or an empty filter slice) -- there
            // is no `since` baseline to tail from. Reusing handleGetFeedStream verbatim handles
            // fleet_dark/filter_empty identically, is the SAME recovery path D-08's overflow
            // reset needs (one recovery path, not two), and is what makes feed.latte's own
            // empty-state promise ("activity will appear here automatically") honest.
            await fetchStream(false, stampUpdatedLine);
        } else {
            await fetchTail(since);
        }
    } catch (err) {
        // (verified precedent, mc.ts:737-739): this branch is a belt-and-suspenders guard --
        // both fetchStream() and fetchTail() already swallow their own fetch errors internally
        // (D-04), so this only fires on a genuinely unexpected synchronous throw.
        console.error('[mc-feed] Poll tick error:', err);
    } finally {
        schedulePollTick();
    }
}

// ---------------------------------------------------------------------------
// Control bindings — each scoped to its OWN container id (D-05 double-bind guard)
// ---------------------------------------------------------------------------

function bindStreamRange(): void {
    const container = document.getElementById('mc-feed-stream-range');
    if (!container) return;

    container.querySelectorAll<HTMLElement>('.mc-range-btn').forEach((btn) => {
        btn.addEventListener('click', () => {
            const range = btn.dataset.range;
            if (!range) return;

            // 243-REVIEW.md CR-01: a row queued under the previous range must not survive
            // into the newly-selected range once flushed.
            resetTailState();
            currentStreamRange = range;
            streamOffset = 0;

            container.querySelectorAll<HTMLElement>('.mc-range-btn').forEach((b) => {
                b.classList.remove('btn-primary');
                b.classList.add('btn-outline-secondary');
            });
            btn.classList.remove('btn-outline-secondary');
            btn.classList.add('btn-primary');

            void fetchStream(false);
        });
    });
}

function bindTypeFilter(): void {
    const container = document.getElementById('mc-feed-type-filter');
    if (!container) return;

    container.querySelectorAll<HTMLElement>('[data-type]').forEach((btn) => {
        btn.addEventListener('click', () => {
            const type = btn.dataset.type;
            if (!type) return;

            // 243-REVIEW.md CR-01: a row queued under the previous type filter must not
            // survive into the newly-selected filter once flushed.
            resetTailState();
            currentType = type;
            streamOffset = 0;

            container.querySelectorAll<HTMLElement>('[data-type]').forEach((b) => {
                b.classList.remove('btn-primary');
                b.classList.add('btn-outline-secondary');
            });
            btn.classList.remove('btn-outline-secondary');
            btn.classList.add('btn-primary');

            void fetchStream(false);
        });
    });
}

function bindLoadMore(): void {
    const btn = document.getElementById('mc-feed-stream-load-more');
    if (!btn) return;

    btn.addEventListener('click', () => {
        void fetchStream(true);
    });
}

/** D-11: mirrors `bindLoadMore()`'s shape exactly -- the pill's one click behavior is the
 *  shared `flushArrivalQueue()` path, always with a scroll-to-head. */
function bindArrivalPill(): void {
    const btn = document.getElementById('mc-feed-arrival-pill');
    if (!btn) return;

    btn.addEventListener('click', () => {
        flushArrivalQueue(true);
    });
}

function bindChatRange(): void {
    const container = document.getElementById('mc-feed-chat-range');
    if (!container) return;

    container.querySelectorAll<HTMLElement>('.mc-range-btn').forEach((btn) => {
        btn.addEventListener('click', () => {
            const range = btn.dataset.range;
            if (!range) return;

            currentChatRange = range;

            container.querySelectorAll<HTMLElement>('.mc-range-btn').forEach((b) => {
                b.classList.remove('btn-primary');
                b.classList.add('btn-outline-secondary');
            });
            btn.classList.remove('btn-outline-secondary');
            btn.classList.add('btn-primary');

            void fetchChatVolume();
        });
    });
}

// ---------------------------------------------------------------------------
// Activity stream (§7.3)
// ---------------------------------------------------------------------------

/**
 * `onSuccess` is an optional Phase 243 hook (never used by the pre-existing initial-load /
 * filter-change / Load-more call sites, which all still call this with one argument) -- it lets
 * `pollTick()`'s D-14 zero-baseline branch stamp the D-16 Updated line ONLY when this page-0
 * fetch actually rendered, without duplicating this function's own fetch/render logic.
 */
async function fetchStream(append: boolean, onSuccess?: () => void): Promise<void> {
    const seq = ++streamSeq;

    // D-07: prepending K rows shifts the server's positional window down by K, so an
    // uncorrected offset=25 re-serves K rows the client already holds; with D-06's dedup those
    // would be dropped and the symptom is a SHORT PAGE, not duplicates or loss. Purely
    // client-side arithmetic -- the ?do=getFeedStream wire contract is untouched.
    const offset = append ? (compensatedOffset(streamNextOffset, offsetCompensation) ?? 0) : 0;
    const url = `?do=getFeedStream&range=${encodeURIComponent(currentStreamRange)}`
        + `&type=${encodeURIComponent(currentType)}`
        + `&offset=${offset}`
        + serverQueryTerms();

    try {
        const resp = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!resp.ok) throw new Error(String(resp.status));

        const payload: StreamPayload = await resp.json();
        if (seq !== streamSeq) return; // a newer request has already been issued
        renderStream(payload, append);
        onSuccess?.();
    } catch (err) {
        console.error('[mc-feed] Stream fetch error:', err);
        if (seq === streamSeq && !append) renderStreamError();
    }
}

/**
 * D-05's tail fetch. Opens with `++streamSeq` -- the SAME module counter `fetchStream()` uses,
 * never a second `tailSeq` -- because `refetchAll()`'s own `void fetchStream(false)` already
 * increments `streamSeq`, so a tail response still in flight when the viewer changes a filter
 * correctly loses instead of prepending the previous filter's rows onto the new filter's list.
 */
async function fetchTail(sinceIso: string): Promise<void> {
    const seq = ++streamSeq;

    const url = `?do=getFeedTail&since=${encodeURIComponent(sinceIso)}`
        + `&range=${encodeURIComponent(currentStreamRange)}`
        + `&type=${encodeURIComponent(currentType)}`
        + serverQueryTerms();

    try {
        const resp = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!resp.ok) throw new Error(String(resp.status));

        const payload: TailPayload = await resp.json();
        if (seq !== streamSeq) return; // a newer request (e.g. a filter change) has since landed

        applyTailPayload(payload);
        stampUpdatedLine();
    } catch (err) {
        // D-04: a dropped tail request must never wipe a populated pane -- console.error and
        // change NOTHING in the DOM. Error cards (renderStreamError()) stay reserved for the
        // initial load, where there is nothing to preserve -- direct 240 D-16 lineage: a
        // renderer that cannot render honestly declines rather than clobbering good markup.
        console.error('[mc-feed] Tail fetch error:', err);
    }
}

/**
 * D-16: the quiet "Updated ‹relative›" liveness line under the stream header. Populates/updates
 * `#mc-feed-updated` on every SUCCESSFUL poll fetch (tail or D-14 fallback) and on no other path
 * -- D-04 makes poll failures silent, so this line is the one visible tell that distinguishes a
 * permanently dead poll from a genuinely quiet fleet (T-243-09).
 */
function stampUpdatedLine(): void {
    const container = document.getElementById('mc-feed-updated');
    if (!container) return;

    let timeEl = document.getElementById('mc-feed-updated-time') as HTMLTimeElement | null;
    if (!timeEl) {
        container.appendChild(document.createTextNode('Updated '));
        timeEl = document.createElement('time');
        timeEl.id = 'mc-feed-updated-time';
        container.appendChild(timeEl);
    }

    // A CLIENT-generated instant, not a wire value -- no wireInstantToUtcIso() reinterpretation
    // applies. Same "populate on creation, don't wait for the 60s ticker" idiom streamRowEl()
    // already uses.
    timeEl.dataset.utc = new Date().toISOString();
    localizeInstant(timeEl);
    // Keeps reading as elapsed time past the 24h relative-time cutover instead of flipping to an
    // absolute date, matching detail.latte's "Status as of" precedent.
    timeEl.dataset.relative = 'always';
}

function renderStream(payload: StreamPayload, append: boolean): void {
    const container = document.getElementById('mc-feed-stream');
    if (!container) return;

    if (!payload.has_data) {
        streamNextOffset = null;
        hideLoadMore();

        if (payload.empty_kind === 'filter_empty') {
            container.replaceChildren(emptyCard(
                'Nothing here for this filter',
                `No ${typeFilterNoun()} in the selected window. Try a wider range or a different filter.`,
            ));
        } else {
            container.replaceChildren(emptyCard(
                "Fleet activity isn't live yet",
                "No server is reporting Lane A events yet. Once a server's mod sends its first "
                + 'death, kill or session event, activity will appear here automatically.',
            ));
        }
        // Mandatory even though there are no rows to draw a connector for: without this, a
        // previous render's entries would survive an empty result, and the next prepend would
        // splice new rows into a stale array pointing at detached elements. No arc-recompute
        // call is needed here — there is nothing to recompute.
        renderedRows = [];
        return;
    }

    const entries = payload.rows.map((r) => ({ row: r, el: streamRowEl(r) }));
    if (append) {
        entries.forEach((entry) => container.appendChild(entry.el));
        renderedRows.push(...entries);
        recomputeArcs();
        // Phase 276 Plan 09 (D-09, QNAME-04): a Load-more append happens well after mc.ts's own
        // one-time initQuestDetail() page-load pass has already run, so each newly-appended row's
        // own quest-detail trigger(s) (if any) need their own bind here — per-entry, not per-
        // container, so this query never re-walks rows a previous append already bound.  Missing
        // this insertion path would leave a row's cue visible but its popover permanently
        // unopenable, which is worse than no cue at all. attachQuestDetailTriggers() is safe to
        // call repeatedly (its own Popover.getInstance() guard, see questDetail.ts) — this file
        // constructs no Popover itself.
        entries.forEach((entry) => {
            void attachQuestDetailTriggers(entry.el).catch((err: unknown) => {
                console.error('[mc-feed] attachQuestDetailTriggers() failed (append):', err);
            });
        });
    } else {
        container.replaceChildren(...entries.map((e) => e.el));
        renderedRows = entries;
        offsetCompensation = 0; // D-07: a page-0 render re-baselines the offset window

        // D-05/D-06 baseline seed: a page-0 render is the ONLY place this client's row set is
        // ever (re)established from scratch (initial load, or a filter/server change via
        // refetchAll()'s resetTailState()). Nothing else ever sets newestKnownOccurredAtMs --
        // applyTailPayload() only ADVANCES it -- so without seeding it here pollTick() would
        // stay on D-14's zero-baseline branch forever, even once real rows are on screen.
        // recordSeenIds() must run too: D-06's inclusive boundary means the very first tail
        // fetch WILL re-return the newest already-rendered row(s) at the exact watermark
        // instant, and the dedup set (not the boundary operator) is what stops that from
        // rendering as a duplicate.
        newestKnownOccurredAtMs = advanceWatermark(payload.rows, newestKnownOccurredAtMs);
        recordSeenIds(payload.rows.map((r) => r.id), renderedEventIds);
        recomputeArcs();

        // Phase 276 Plan 09 (D-09, QNAME-04): the Activity Feed's page-0 render happens via this
        // ASYNC fetch (`fetchStream(false)`), which lands well after mc.ts's own one-time
        // `initQuestDetail()` page-load pass already ran and found zero triggers (this container
        // is still empty at that point) — so this `replaceChildren()` render is itself the FIRST
        // opportunity to bind any trigger this page ever gets. One call against the whole
        // container, since every row here is newly built. Same no-Popover-of-its-own discipline
        // as the append branch above.
        void attachQuestDetailTriggers(container).catch((err: unknown) => {
            console.error('[mc-feed] attachQuestDetailTriggers() failed (page-0 render):', err);
        });
    }

    streamNextOffset = payload.next_offset;
    if (streamNextOffset !== null) {
        showLoadMore();
    } else {
        hideLoadMore();
    }
}

function renderStreamError(): void {
    const container = document.getElementById('mc-feed-stream');
    if (!container) return;

    hideLoadMore();
    container.replaceChildren(emptyCard(
        "Couldn't load this activity stream",
        'The activity stream could not be refreshed just now. Reload the page to try again.',
    ));
    renderedRows = [];
}

/**
 * D-10: "at the head" means the stream container's top edge is still in the viewport.
 * `#mc-feed-stream` is a plain page-flow `div` with no `overflow` and no `max-height` (confirmed
 * against `mc.scss`), so this is a WINDOW-scroll test, not a container-scroll test.
 *
 * Two rejected alternatives, deliberately not named by their literal property here (a grep
 * enforces their absence from this function, mirroring this file's own header-comment
 * discipline): a raw vertical-offset-under-100 check fails at 320px because the `<h2>`, the
 * server picker and two button groups push the stream head well past 100px, so a viewer looking
 * straight at the feed head would wrongly get the pill; and a strict zero-vertical-offset check
 * makes a one-pixel scroll flip live-versus-gated and reads as flaky.
 */
function isAtStreamHead(): boolean {
    const el = document.getElementById('mc-feed-stream');
    if (!el) return false;
    return el.getBoundingClientRect().top >= 0;
}

/**
 * The ONE recovery path, shared by D-08 tail overflow, the `PILL_QUEUE_CAP` case and the
 * `STREAM_ROW_CAP` case. Prepending a partial batch would leave a silent hole in the middle of
 * the list that the viewer can neither see nor reach by scrolling; a fresh page 0 is contiguous
 * by construction. `renderStream()`'s existing non-append branch already does the
 * `replaceChildren()`, so it is not re-implemented here.
 */
function resetStreamToPage0(): void {
    resetTailState();
    streamOffset = 0;
    streamNextOffset = null;
    void fetchStream(false);
}

/**
 * Builds elements with the EXISTING `streamRowEl()` (unchanged) and inserts them with a single
 * `prepend()` call. Rows arrive DESC-ordered from the model and `prepend()` preserves argument
 * order, so newest-at-top is maintained with no reversal step.
 *
 * `flash` (D-12, default false) marks each inserted row with the new-row accent-ring class,
 * applied ONLY at this call site -- never inside `streamRowEl()` itself, which the initial
 * page-0 render and the Load-more append also share, so only a genuinely just-arrived tail row
 * may flash. K/D, Rarity and Firsts never get this flash either: their D-01/D-02 refresh is a
 * wholesale `replaceChildren()` of the whole pane, so there is no reliable "this specific row is
 * new" signal there, and flashing an entire freshly rebuilt table on every aggregate refresh
 * would be noise, not signal. Recent Firsts shares the Activity Stream's row markup but does not
 * get incremental prepends, so it does not get this flash either.
 */
function prependStreamRows(rows: StreamRow[], flash: boolean = false): void {
    const container = document.getElementById('mc-feed-stream');
    if (!container) return;

    const entries = rows.map((r) => ({ row: r, el: streamRowEl(r) }));
    if (flash) {
        const flashClass = 'mc-feed-stream-row--new';
        entries.forEach((entry) => {
            entry.el.classList.add(flashClass);
            // Removed again once the one-shot CSS animation completes, so the marker never
            // lingers as a stale hook for anything that might later key off it; the listener
            // option below lets it clean itself up without a separate teardown call.
            entry.el.addEventListener('animationend', () => {
                entry.el.classList.remove(flashClass);
            }, { once: true });
        });
    }
    container.prepend(...entries.map((e) => e.el));
    offsetCompensation += rows.length; // D-07
    renderedRows.unshift(...entries);
    recomputeArcs();

    // Phase 276 Plan 09 (D-09, QNAME-04): the live-tail insertion path — a row arriving here via
    // polling was never touched by the page-0 `renderStream()` bind above, so it needs its own
    // pass, per-entry so a busy live tail never re-walks rows a previous tick already bound.
    // Missing this insertion path is the same "cue visible, popover unopenable" defect the other
    // two call sites' own comments describe.
    entries.forEach((entry) => {
        void attachQuestDetailTriggers(entry.el).catch((err: unknown) => {
            console.error('[mc-feed] attachQuestDetailTriggers() failed (live-tail prepend):', err);
        });
    });
}

/**
 * Phase 250 Plan 04 (D-14): builds one lane-segment element from a pure `LaneSegment` (the
 * `feedArcs.ts` output). Every part of the class string comes from that module's closed
 * string-literal unions and `seg.lane` is always an integer — no payload-derived string ever
 * reaches a class name or a style value here (T-250-17).
 */
function laneSegmentEl(seg: LaneSegment): HTMLElement {
    const el = document.createElement('span');
    let className = `mc-feed-lane mc-feed-lane--${seg.kind}`;
    if (seg.endpoint !== null) className += ` mc-feed-lane--${seg.endpoint}`;
    if (seg.fadeTop) className += ' mc-feed-lane--fade-top';
    if (seg.fadeBottom) className += ' mc-feed-lane--fade-bottom';
    el.className = className;
    el.style.setProperty('--mc-lane', String(seg.lane));
    return el;
}

/**
 * D-05: re-derives every connector across the COMPLETE currently-loaded row set — a full O(n)
 * re-walk (`laneSegmentsForRows()`), never an incremental patch — and writes the result into
 * every row's lane gutter. Called ONLY from the mutation call sites (page-0 replace, Load More
 * append, live-tail prepend), never from inside `streamRowEl()` itself, mirroring the flash-class
 * discipline `prependStreamRows()` already documents.
 *
 * Deliberately a full re-walk rather than an incremental `Map<playerUuid+serverKey,
 * openJoinRowRef>` index: `STREAM_ROW_CAP` (`utils/feedTail.ts`) already hard-bounds this walk to
 * 300 rows, and an incremental index would need explicit invalidation on
 * `resetStreamToPage0()`'s wholesale replace — a stale-state failure mode a full re-walk cannot
 * have.
 *
 * Rewrites EVERY row's gutter contents every time — including rows whose segment list is
 * empty — which is what guarantees no stale segment survives a recompute; segments are never
 * appended incrementally.
 *
 * Also re-derives the gutter's own width from the real max concurrent lane count (uncapped —
 * `assignLanes()` grows a lane rather than ever reusing one, so this is the count that keeps
 * every rendered lane visually distinct) and writes it to `--mc-lane-count` on the stream
 * container, which `.mc-feed-lane-gutter`/`.mc-feed-stream-row` in mc.scss size themselves from.
 * Set on the container rather than per-row so every row's badge stays aligned regardless of
 * which rows the widest span happens to pass through.
 */
function recomputeArcs(): void {
    const segmentsByRow = laneSegmentsForRows(renderedRows.map((entry) => entry.row));
    let maxLane = -1;
    for (const segments of segmentsByRow) {
        for (const seg of segments) {
            if (seg.lane > maxLane) maxLane = seg.lane;
        }
    }
    const streamEl = document.getElementById('mc-feed-stream');
    streamEl?.style.setProperty('--mc-lane-count', String(Math.max(MIN_LANE_COUNT, maxLane + 1)));

    for (let i = 0; i < renderedRows.length; i++) {
        const gutter = renderedRows[i].el.querySelector('.mc-feed-lane-gutter');
        if (!gutter) continue; // defensive — firstRowEl() rows never enter this array
        gutter.replaceChildren(...(segmentsByRow[i] ?? []).map(laneSegmentEl));
    }
}

/**
 * D-09/D-11: reads `#mc-feed-arrival-pill` and writes ONLY the locked count-string label the
 * `feedTail` helper produces from `queuedCount` -- a pure count string from a pure function,
 * never a payload-derived string. Keeping the pill's label source this narrow is what keeps it
 * (and the D-12 live region) free of user content.
 */
function updateArrivalPill(): void {
    const pill = document.getElementById('mc-feed-arrival-pill');
    if (!pill) return;

    if (queuedCount === 0) {
        pill.classList.add('d-none');
        pill.textContent = '';
    } else {
        pill.textContent = arrivalLabel(queuedCount);
        pill.classList.remove('d-none');
    }
}

/**
 * D-12: the count-only live region. Reads `#mc-feed-live-region` and writes ONLY the locked
 * count-string label -- never a row narrative, never a player name, never a concatenation of
 * either. This separation is load-bearing for two reasons: putting `aria-live` on
 * `#mc-feed-stream` itself would dump 25 row narratives into a screen reader on the initial
 * render, on every Load more click and on every D-08 overflow reset; and a count composed from a
 * number and two literal words is the structural guarantee that no attacker-influenced wire
 * string can ever be read aloud (T-243-03).
 */
function announceArrival(count: number): void {
    const region = document.getElementById('mc-feed-live-region');
    if (!region) return;
    region.textContent = arrivalLabel(count);
}

/**
 * D-11: the single flush path used by both the pill click and the auto-flush-on-scroll case.
 * `queueResetPending` routes into `resetStreamToPage0()` (which already clears every piece of
 * tail state) rather than prepending a partial batch -- that would be exactly the silent
 * mid-list hole D-08 exists to prevent, so the `queuedRows` branch below is skipped in that case
 * (it naturally no-ops afterward anyway, since `resetStreamToPage0()` clears `queuedRows` too).
 */
function flushArrivalQueue(scrollToHead: boolean): void {
    if (queueResetPending) {
        resetStreamToPage0();
    } else if (queuedRows.length > 0) {
        prependStreamRows(queuedRows, true);
        queuedRows = [];
        queuedCount = 0;
    }

    updateArrivalPill();

    if (scrollToHead) {
        const streamEl = document.getElementById('mc-feed-stream');
        if (streamEl) {
            // Mandatory, not optional: forcing a smooth scroll on a reduced-motion viewer is the
            // one genuinely new motion surface this phase adds and it has no in-repo precedent,
            // so the guard is explicit rather than left to a CSS scroll-behavior default.
            streamEl.scrollIntoView({
                block: 'start',
                behavior: prefersReducedMotion() ? 'auto' : 'smooth',
            });
        }
    }
}

/** The D-06/D-07/D-08/D-09/D-10 decision funnel applied to one tail response. */
function applyTailPayload(payload: TailPayload): void {
    // D-06: dedup BEFORE anything else touches the DOM -- the inclusive >= boundary
    // deliberately re-delivers the watermark second every tick, and this is what makes that
    // safe.
    const fresh = selectUnseenRows(payload.rows, renderedEventIds);

    if (fresh.length === 0 && !payload.overflowed) {
        // D-02's idle tick: nothing new arrived, nothing changed. Plan 243-05's aggregate
        // refetches must not fire.
        return;
    }

    const container = document.getElementById('mc-feed-stream');
    const atHead = isAtStreamHead();
    // When the viewer is at the head, any rows queued while they were scrolled away flush
    // together with this tick's arrivals, so the list never silently withholds rows once the
    // viewer comes back.
    const incoming = atHead ? queuedRows.concat(fresh) : fresh;

    if (shouldResetToPage0({
        overflowed: payload.overflowed,
        domRowCount: container ? container.childElementCount : 0,
        incomingCount: incoming.length,
        queueResetPending,
    })) {
        resetStreamToPage0();
        return;
    }

    let deliveredCount = 0; // D-12: the count this tick actually delivered to the viewer

    if (atHead) {
        prependStreamRows(incoming, true);
        recordSeenIds(incoming.map((r) => r.id), renderedEventIds);
        newestKnownOccurredAtMs = advanceWatermark(incoming, newestKnownOccurredAtMs);
        queuedRows = [];
        queuedCount = 0;
        queueResetPending = false;
        // A pill left over from a previous scroll position must disappear once the viewer is
        // back at the head, even though this tick's own rows never touched the queue.
        updateArrivalPill();
        deliveredCount = incoming.length;
    } else {
        queuedRows.push(...fresh);
        queuedCount += fresh.length;
        // Recording ids and advancing the watermark for QUEUED rows is deliberate: the tail must
        // not re-deliver them on the next tick just because they have not been rendered yet.
        recordSeenIds(fresh.map((r) => r.id), renderedEventIds);
        newestKnownOccurredAtMs = advanceWatermark(fresh, newestKnownOccurredAtMs);

        if (queuedRows.length > PILL_QUEUE_CAP) {
            // Leave queuedCount intact so the announced number stays honest -- the eventual
            // flush then takes resetStreamToPage0() rather than prepending a partial batch.
            queuedRows = [];
            queueResetPending = true;
        }
        updateArrivalPill();
        deliveredCount = fresh.length;
    }

    // D-12: announce the count ONLY -- never a row narrative, never a player name. This is the
    // single call site for the whole tick; flushArrivalQueue() does NOT call this, because those
    // rows were already announced here when they first arrived, and re-announcing them on a
    // later flush would double-announce.
    if (deliveredCount > 0) {
        announceArrival(deliveredCount);
    }

    // D-01: a tick touches four panes and only four -- the Activity Stream (above), K/D Ranking,
    // Rarest Advancements and Recent Firsts (below). The two Chat Highcharts panes (volumeChart,
    // hoursChart) and the Word Rank DataTable (wordRankTable) are deliberately excluded:
    // re-creating a Highcharts instance every 30s is wasteful and flickers, and redrawing a
    // client-side DataTable destroys the viewer's sort, page and search state -- the same "once
    // per page load, never per tick" discipline initDetail()'s own comment applies to its own
    // resource-history chart. This is a recorded design choice with a known cost, not an
    // oversight; do not "fix" it back later. Never call fetchChatVolume, fetchChatHours or
    // fetchChatWordRank from any poll path, and never touch volumeChart, hoursChart or
    // wordRankTable from one either.
    //
    // D-02: these are public, unauthenticated signals against the shared stats_db that Phase 237
    // was an incident phase about, so "one request per idle tick, not four" is a real constraint,
    // not premature optimisation. This block sits AFTER the zero-new-rows early return above, so
    // an idle tick costs exactly one request (the tail itself), never four.
    //
    // D-03: the type-aware mapping below (never an inline equivalent) carries its own two
    // accepted blind spots in its doc comment in feedTail.ts -- not re-litigated here.
    const targets = aggregateRefetchTargets(fresh);
    if (targets.kd) void fetchKd(true);
    if (targets.rarity) void fetchRarity(true);
    if (targets.firsts) void fetchFirsts(true);
}

function typeFilterNoun(): string {
    switch (currentType) {
        case 'death': return 'deaths';
        case 'kill': return 'kills';
        case 'session': return 'sessions';
        default: return 'events';
    }
}

function streamRowEl(row: StreamRow): HTMLElement {
    const el = document.createElement('div');
    el.className = 'mc-feed-stream-row';

    // Phase 250 Plan 04 (D-01..D-07, D-13): the swimlane connector's per-row anchor. Absolutely
    // positioned by src/css/mc.scss (Plan 250-03), so its position in this row's child list has
    // no effect on layout — appended first purely for source-order clarity. Emitted UNCONDITIONALLY
    // on every row this builder produces (join, quit, death, kill alike): if the gutter only
    // appeared on rows carrying a lane, every other row's badge/avatar/narrative would shift
    // horizontally depending on which arcs happen to pass through it, and switching the type
    // filter (D-07) would visibly jump every remaining row. Starts empty; the arc-recompute
    // function added by this phase's next plan task is its SOLE later writer.
    const gutter = document.createElement('div');
    gutter.className = 'mc-feed-lane-gutter';
    gutter.setAttribute('aria-hidden', 'true');
    el.appendChild(gutter);

    el.appendChild(badgeEl(row.badge));
    el.appendChild(avatarEl(row.player_uuid, row.player_name));

    const narrative = document.createElement('span');
    narrative.className = 'mc-feed-stream-narrative';
    // D-01/D-02 (Phase 244) origin, restructured by Phase 254 D-07: this guard is still defensive
    // rather than "trust the field is always present" -- an already-loaded page can poll a
    // freshly-deployed backend, or vice versa, for the length of every deploy window. Segments
    // present means real pills spliced into the sentence via the shared coordPill.ts renderer
    // (Rule A: pill only when that segment's own href resolves, plain text otherwise); segments
    // absent means a pre-254 backend, so the row falls back to the legacy flat `narrative` string.
    // Neither branch may ever render `undefined`.
    if (Array.isArray(row.narrative_segments)) {
        appendNarrativeSegments(narrative, row.narrative_segments, { mode: 'link' });
    } else {
        narrative.textContent = row.narrative ?? '';
    }
    // D-08..D-12: the inline server icon+name, gated on row.badge (this module's own row-kind
    // vocabulary, BADGE_CLASS/BADGE_LABEL) rather than row.event_type. Death/kill rows carry the
    // three identity fields per D-11 but render nothing this phase. Guarded defensively the same
    // way the bluemap_href block above is: a row arriving from a pre-Plan-250-01 backend during a
    // deploy window has server_name === undefined, so the whole block is skipped rather than
    // rendering a bare space or an empty anchor.
    if (
        (row.badge === 'session-join' || row.badge === 'session-quit')
        && typeof row.server_name === 'string' && row.server_name !== ''
    ) {
        appendInlineServerIcon(narrative, row);
    }
    el.appendChild(narrative);

    const time = document.createElement('span');
    time.className = 'mc-feed-stream-time';
    time.append('· ');
    const timeEl = document.createElement('time');
    // T-240-15: a non-finite epoch would make the Date-to-ISO-string conversion throw, aborting
    // the whole row build -- guard it so an invalid value degrades to an empty <time> body
    // (localizeInstant() itself already no-ops on a missing data-utc) rather than breaking the
    // feed.
    const occurredAtMs = row.occurred_at_ms;
    if (Number.isFinite(occurredAtMs)) {
        timeEl.dataset.utc = new Date(occurredAtMs).toISOString();
        localizeInstant(timeEl); // D-03: populate on creation, don't wait for the 60s ticker
    }
    time.appendChild(timeEl);
    el.appendChild(time);

    // Phase 272 (D-09/D-11/PC-3, SURF-02): the forensics expand chevron + Collapse panel —
    // appended ONLY when this row actually carries a recoverable grave record. A strict `===
    // true` identity check (not merely truthy) so `undefined`/`null`/`0`/`'0'` all fall through
    // to "no chevron, no disabled chevron, no placeholder" — PC-3's ~61%-of-deaths-carry-no-grave
    // majority path must render with zero trace of this affordance, the same class of rejection
    // D-04 already applied to the dead coordinate pill.
    if (row.has_forensics === true) {
        el.dataset.hasForensics = 'true';
        appendForensicsExpand(el, row);
    }

    return el;
}

/**
 * D-09/D-11 (Phase 272, SURF-02): appends the `.mc-feed-forensics-toggle` chevron button and its
 * `.mc-feed-forensics-panel` Collapse target to a death row that carries `has_forensics: true`.
 * Both are anchored to THIS row's own element (`el`) and this row's own stable `row.id` — never a
 * DOM index or a position in `renderedRows`, either of which `prependStreamRows()`'s
 * `container.prepend(...)` would invalidate. Because the panel is a genuine child of `el`, an
 * already-expanded panel is carried along unmodified whenever a live-tail prepend or a Load-more
 * append inserts OTHER rows around it — this function only ever runs for a row being newly built,
 * never for a row already on screen, so an existing expansion is never touched by either
 * mechanic. `recomputeArcs()` (above) reads only `.mc-feed-lane-gutter` — a sibling this function
 * does not touch — and the gutter's own `position: absolute; top: 0; bottom: 0;` already stretches
 * to match a taller expanded row via plain CSS, so no re-run is needed here.
 *
 * The chevron is NOT a Bootstrap accordion-button (no `data-bs-toggle` markup), so — unlike the
 * Graves accordion's server-rendered buttons — `aria-expanded`/`aria-label` are wired by hand,
 * synchronously in the click handler, never deferred to a Collapse lifecycle event (whose
 * transition-complete timing is not guaranteed inside a short test window).
 */
function appendForensicsExpand(el: HTMLElement, row: StreamRow): void {
    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'mc-feed-forensics-toggle btn btn-sm btn-link p-0 ms-1 text-decoration-none';
    toggle.setAttribute('aria-expanded', 'false');
    toggle.setAttribute('aria-label', 'Expand forensics');
    // A bare caret glyph — this codebase has no icon library (272-UI-SPEC.md's Design System
    // table), and the accessible name lives entirely in aria-label above, not in this text.
    toggle.textContent = '⌄';

    const panel = document.createElement('div');
    // `w-100` (a plain Bootstrap utility, zero new CSS) forces the panel onto its own line inside
    // `.mc-feed-stream-row`'s `flex-wrap: wrap` layout, the same trick this codebase already
    // relies on Bootstrap for elsewhere — no mc.scss change required by this plan.
    panel.className = 'mc-feed-forensics-panel collapse w-100';
    panel.id = `mc-feed-forensics-${row.id}`;

    const collapseInstance = new Collapse(panel, { toggle: false });

    // D-11: the module's own per-death-id cache means a second expand never re-fetches — this
    // flag only stops a redundant `renderInto()` call (and its brief loading-copy flash) on an
    // already-filled panel, never a second network request.
    let loaded = false;

    toggle.addEventListener('click', () => {
        const expanding = toggle.getAttribute('aria-expanded') !== 'true';
        if (expanding) {
            toggle.setAttribute('aria-expanded', 'true');
            toggle.setAttribute('aria-label', 'Collapse forensics');
            if (!loaded) {
                loaded = true;
                void renderInto(panel, row.id);
            }
            collapseInstance.show();
        } else {
            toggle.setAttribute('aria-expanded', 'false');
            toggle.setAttribute('aria-label', 'Expand forensics');
            collapseInstance.hide();
        }
    });

    el.appendChild(toggle);
    el.appendChild(panel);
}

/**
 * D-08..D-12: appends the inline 32px server icon + name to a `session-join`/`session-quit`
 * row's narrative — a direct port of `allStats.ts`'s `buildServerCell()` icon-tile construction,
 * adapted for inline text flow (one anchor wrapping both the icon and the name, literal
 * text-node spacing instead of a flex-cell layout).
 *
 * Renders UNCONDITIONALLY per D-10 — never hidden because the multi-select server picker happens
 * to be scoped to a single server; the icon identifies which server THIS row's event happened on,
 * independent of the current filter selection.
 */
function appendInlineServerIcon(narrative: HTMLElement, row: StreamRow): void {
    const tile = document.createElement('span');
    tile.className = 'mc-server-icon-tile mc-server-icon-tile--inline';

    if (row.server_icon !== null) {
        const img = document.createElement('img');
        // `row.server_icon` is used DIRECTLY with no scheme check, no allow-list, no URL parsing
        // and no sanitizer of any kind — the value arrives pre-sanitized by
        // `App\Mc\FaviconUrl::sanitize()` server-side (T-249-I-01) and that is the SOLE control.
        // A second client-side check is explicitly forbidden: it would only create a second place
        // for the rule to drift out of sync with the one real control.
        img.src = row.server_icon;
        img.loading = 'lazy';
        img.width = 32;
        img.height = 32;
        img.alt = '';
        // Visibility, not display — the placeholder background shows through with zero layout
        // shift, matching buildServerCell()'s identical guard.
        img.addEventListener('error', () => { img.style.visibility = 'hidden'; });
        tile.appendChild(img);
    }

    // A text node only — never a markup-string assignment of any kind (T-232-03).
    const name = document.createTextNode(row.server_name);

    // Same literal-text-node spacing idiom the bluemap_href block above uses — not a CSS margin.
    narrative.appendChild(document.createTextNode(' '));

    if (row.server_key === '') {
        // Direct port of buildServerCell()'s `if (row.server_key === '') return tile;` branch,
        // extended to also skip wrapping the name — icon and name render with no click target.
        narrative.appendChild(tile);
        narrative.appendChild(document.createTextNode(' '));
        narrative.appendChild(name);
        return;
    }

    const link = document.createElement('a');
    // encodeURIComponent is mandatory — it is what keeps a crafted key from breaking out of the
    // fixed same-origin '/mc/' path segment (T-250-14).
    link.href = '/mc/' + encodeURIComponent(row.server_key);
    link.title = row.server_name;
    link.setAttribute('aria-label', row.server_name);
    // ONE anchor wraps BOTH the icon and the name (UI-SPEC's Click Target row) — not two separate
    // clickable spans.
    link.appendChild(tile);
    link.appendChild(document.createTextNode(' '));
    link.appendChild(name);
    // No target/rel — this is a same-origin internal route, unlike the bluemap link above it
    // which opens externally.
    narrative.appendChild(link);
}

/** Head-only avatar tile linking to the player's profile — mirrors playerLinkEl()'s tile half
 *  (same mc-heads.net source, same D-12 hidden-on-404 fallback), for stream/firsts rows that
 *  already carry the name in their narrative prose and don't need a second name label. */
function avatarEl(uuid: string, name: string): HTMLElement {
    const tile = document.createElement('a');
    tile.href = `/mc/players/${encodeURIComponent(uuid)}`;
    tile.title = name;
    tile.className = 'mc-avatar-tile';

    const img = document.createElement('img');
    img.src = `https://mc-heads.net/avatar/${encodeURIComponent(uuid)}/32`;
    img.loading = 'lazy';
    img.width = 32;
    img.height = 32;
    img.alt = '';
    img.className = 'mc-player-avatar';
    img.addEventListener('error', () => { img.style.visibility = 'hidden'; });
    tile.appendChild(img);

    return tile;
}

function badgeEl(kind: string): HTMLElement {
    const badge = document.createElement('span');
    badge.className = `mc-feed-badge ${BADGE_CLASS[kind] ?? ''}`.trim();
    badge.textContent = BADGE_LABEL[kind] ?? kind;
    return badge;
}

function showLoadMore(): void {
    const btn = document.getElementById('mc-feed-stream-load-more');
    if (btn) btn.classList.remove('d-none');
}

function hideLoadMore(): void {
    const btn = document.getElementById('mc-feed-stream-load-more');
    if (btn) btn.classList.add('d-none');
}

// ---------------------------------------------------------------------------
// K/D Ranking (§7.4, D-08)
// ---------------------------------------------------------------------------

/**
 * `fromPoll` (D-04, 240 D-16 lineage): an error card is correct on the initial load, where there
 * is nothing to preserve, and destructive on a poll, where one dropped request would wipe a
 * populated pane -- a renderer that cannot render honestly declines rather than clobbering good
 * markup. UI-SPEC §6: on a poll-tick failure there is no toast, no banner, no dimming, no border
 * colour change and no icon -- the Updated line simply stops advancing, and that is the entire
 * visible consequence by design. `refetchAll()`'s call sites pass nothing and keep today's
 * initial-load behaviour byte-for-byte.
 */
async function fetchKd(fromPoll: boolean = false): Promise<void> {
    const seq = ++kdSeq;
    const url = `?do=getKd${serverQueryTerms()}`;

    try {
        const resp = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!resp.ok) throw new Error(String(resp.status));

        const payload: KdPayload = await resp.json();
        if (seq !== kdSeq) return; // a newer request has already been issued
        renderKd(payload);
    } catch (err) {
        console.error('[mc-feed] K/D fetch error:', err);
        if (seq === kdSeq && !fromPoll) renderKdError();
    }
}

function renderKd(payload: KdPayload): void {
    const container = document.getElementById('mc-feed-kd-table');
    if (!container) return;

    if (!payload.has_data) {
        container.replaceChildren(emptyCard(
            'Nothing to rank yet',
            'No kill or death events have been recorded yet.',
        ));
        return;
    }

    container.replaceChildren(kdTableEl(payload.rows));
}

function renderKdError(): void {
    const container = document.getElementById('mc-feed-kd-table');
    if (!container) return;

    container.replaceChildren(emptyCard(
        "Couldn't load this K/D ranking",
        'The K/D ranking could not be refreshed just now. Reload the page to try again.',
    ));
}

function kdTableEl(rows: KdRow[]): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'table-responsive';

    const table = document.createElement('table');
    table.className = 'table table-sm table-striped mc-board-table';

    const thead = document.createElement('thead');
    const headRow = document.createElement('tr');
    headRow.appendChild(th('Rank', 'mc-board-rank-col'));
    headRow.appendChild(th('Player'));
    headRow.appendChild(th('Kills', 'text-end'));
    headRow.appendChild(th('Deaths', 'text-end'));
    headRow.appendChild(th('K/D', 'text-end'));
    thead.appendChild(headRow);
    table.appendChild(thead);

    const tbody = document.createElement('tbody');
    for (const row of rows) {
        const tr = document.createElement('tr');
        tr.dataset.uuid = row.player_uuid;

        const rankTd = document.createElement('td');
        rankTd.className = 'mc-board-rank';
        appendRankCell(rankTd, row.rank);
        tr.appendChild(rankTd);

        const playerTd = document.createElement('td');
        playerTd.appendChild(playerLinkEl(row.player_uuid, row.player_name));
        tr.appendChild(playerTd);

        const killsTd = document.createElement('td');
        killsTd.className = 'mc-board-value';
        killsTd.textContent = formatCount(row.kills);
        tr.appendChild(killsTd);

        const deathsTd = document.createElement('td');
        deathsTd.className = 'mc-board-value';
        deathsTd.textContent = formatCount(row.deaths);
        tr.appendChild(deathsTd);

        const kdTd = document.createElement('td');
        kdTd.className = 'mc-board-value';
        // ABSENT IS NOT ZERO — zero deaths is an undefined ratio, never a coerced 0.0
        // (matches leaderboard.ts's own appendValueCell() rule, D-08).
        if (row.kd === null) {
            const dash = document.createElement('span');
            dash.className = 'mc-board-unmeasured';
            dash.textContent = '—';
            dash.title = 'No deaths recorded for this player — an undefined ratio, not zero.';
            kdTd.appendChild(dash);
        } else {
            kdTd.textContent = row.kd.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });
        }
        tr.appendChild(kdTd);

        tbody.appendChild(tr);
    }
    table.appendChild(tbody);

    wrap.appendChild(table);
    return wrap;
}

// ---------------------------------------------------------------------------
// Rarest Advancements (§7.5a, D-09/D-10, Pitfall 5 — never a % sign)
// ---------------------------------------------------------------------------

/** `fromPoll` -- same D-04 split as `fetchKd()` above; see its doc comment for the full rationale. */
async function fetchRarity(fromPoll: boolean = false): Promise<void> {
    const seq = ++raritySeq;
    const url = `?do=getRarity${serverQueryTerms()}`;

    try {
        const resp = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!resp.ok) throw new Error(String(resp.status));

        const payload: RarityPayload = await resp.json();
        if (seq !== raritySeq) return; // a newer request has already been issued
        renderRarity(payload);
    } catch (err) {
        console.error('[mc-feed] Rarity fetch error:', err);
        if (seq === raritySeq && !fromPoll) renderRarityError();
    }
}

function renderRarity(payload: RarityPayload): void {
    const container = document.getElementById('mc-feed-rarity-table');
    if (!container) return;

    if (!payload.has_data) {
        container.replaceChildren(emptyCard(
            'Nothing to rank yet',
            'No advancement has been earned by any player yet.',
        ));
        return;
    }

    container.replaceChildren(rarityTableEl(payload));
}

function renderRarityError(): void {
    const container = document.getElementById('mc-feed-rarity-table');
    if (!container) return;

    container.replaceChildren(emptyCard(
        "Couldn't load this advancement list",
        'The advancement list could not be refreshed just now. Reload the page to try again.',
    ));
}

function rarityTableEl(payload: RarityPayload): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'table-responsive';

    const table = document.createElement('table');
    table.className = 'table table-sm table-striped';

    const thead = document.createElement('thead');
    const headRow = document.createElement('tr');
    headRow.appendChild(th('Rank'));
    headRow.appendChild(th('Advancement'));
    headRow.appendChild(th('Earned by'));
    thead.appendChild(headRow);
    table.appendChild(thead);

    const tbody = document.createElement('tbody');
    for (const row of payload.rows) {
        const tr = document.createElement('tr');

        const rankTd = document.createElement('td');
        rankTd.textContent = String(row.rank);
        tr.appendChild(rankTd);

        const titleTd = document.createElement('td');
        titleTd.textContent = row.title;
        tr.appendChild(titleTd);

        const earnedTd = document.createElement('td');
        // Pitfall 5 — ALWAYS an absolute "{N} of {M} players" count. No proportion character.
        earnedTd.textContent = `${formatCount(row.earned_by)} of ${formatCount(payload.m_players)} players`;
        tr.appendChild(earnedTd);

        tbody.appendChild(tr);
    }
    table.appendChild(tbody);

    wrap.appendChild(table);
    return wrap;
}

// ---------------------------------------------------------------------------
// Recent Firsts (§7.5b) — reuses the Activity Stream's badge+narrative row component
// ---------------------------------------------------------------------------

/** `fromPoll` -- same D-04 split as `fetchKd()` above; see its doc comment for the full rationale. */
async function fetchFirsts(fromPoll: boolean = false): Promise<void> {
    const seq = ++firstsSeq;
    const url = `?do=getFirsts${serverQueryTerms()}`;

    try {
        const resp = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!resp.ok) throw new Error(String(resp.status));

        const payload: FirstsPayload = await resp.json();
        if (seq !== firstsSeq) return; // a newer request has already been issued
        renderFirsts(payload);
    } catch (err) {
        console.error('[mc-feed] Firsts fetch error:', err);
        if (seq === firstsSeq && !fromPoll) renderFirstsError();
    }
}

function renderFirsts(payload: FirstsPayload): void {
    const container = document.getElementById('mc-feed-firsts');
    if (!container) return;

    if (!payload.has_data) {
        container.replaceChildren(emptyCard(
            'No firsts yet',
            'Once a player earns an advancement for the first time on the fleet, it will appear here.',
        ));
        return;
    }

    container.replaceChildren(...payload.rows.map(firstRowEl));
}

function renderFirstsError(): void {
    const container = document.getElementById('mc-feed-firsts');
    if (!container) return;

    container.replaceChildren(emptyCard(
        "Couldn't load this list",
        'The list could not be refreshed just now. Reload the page to try again.',
    ));
}

function firstRowEl(row: FirstsRow): HTMLElement {
    const el = document.createElement('div');
    el.className = 'mc-feed-stream-row';
    el.appendChild(badgeEl('first'));
    el.appendChild(avatarEl(row.player_uuid, row.player_name));

    const narrative = document.createElement('span');
    narrative.className = 'mc-feed-stream-narrative';
    // Locked verbatim copy (UI-SPEC §7.5b): {PlayerName} was first to earn "{Title}" — built as a
    // single textContent assignment (never innerHTML) even though player_name/title are
    // payload-derived.
    narrative.textContent = `${row.player_name} was first to earn "${row.title}"`;
    el.appendChild(narrative);

    const time = document.createElement('span');
    time.className = 'mc-feed-stream-time';
    time.append('· ');
    const timeEl = document.createElement('time');
    // T-240-15: same non-finite-epoch guard as streamRowEl() above.
    const occurredAtMs = row.occurred_at_ms;
    if (Number.isFinite(occurredAtMs)) {
        timeEl.dataset.utc = new Date(occurredAtMs).toISOString();
        localizeInstant(timeEl); // D-03: populate on creation, don't wait for the 60s ticker
    }
    time.appendChild(timeEl);
    el.appendChild(time);

    return el;
}

// ---------------------------------------------------------------------------
// Chat Activity (§7.6, D-11/D-12) — counts only, never the raw message text
// ---------------------------------------------------------------------------

async function fetchChatVolume(): Promise<void> {
    const seq = ++chatVolumeSeq;
    const url = `?do=getChatVolume&range=${encodeURIComponent(currentChatRange)}`
        + serverQueryTerms();

    try {
        const resp = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!resp.ok) throw new Error(String(resp.status));

        const payload: ChatVolumePayload = await resp.json();
        if (seq !== chatVolumeSeq) return; // a newer request has already been issued
        renderChatVolume(payload);
    } catch (err) {
        console.error('[mc-feed] Chat volume fetch error:', err);
        if (seq === chatVolumeSeq) renderChatChartError('mc-feed-chat-volume', () => { volumeChart = null; });
    }
}

function renderChatVolume(payload: ChatVolumePayload): void {
    const container = document.getElementById('mc-feed-chat-volume');
    if (!container) return;

    if (volumeChart) {
        volumeChart.destroy();
        volumeChart = null;
    }

    if (!payload.has_data) {
        container.classList.add('chart-empty');
        container.replaceChildren(...emptyStateNodes(
            'No chat activity yet',
            'No chat messages have been recorded yet. Message content is never shown here — only counts.',
        ));
        return;
    }

    if (!seriesTypeAvailable('column')) {
        console.error('[mc-feed] Highcharts column series unavailable — skipping the chat volume chart.');
        return;
    }

    container.classList.remove('chart-empty');
    container.replaceChildren();

    volumeChart = Highcharts.chart(container, Highcharts.merge(getChartTheme(), {
        chart: { type: 'column', backgroundColor: 'transparent' },
        title: { text: '' },
        xAxis: { type: 'datetime' },
        yAxis: { min: 0, allowDecimals: false, title: { text: 'Messages' } },
        legend: { enabled: false },
        series: [{
            type: 'column',
            name: 'Messages',
            data: payload.points,
            // CHRT-03 / UI-SPEC "Accent reserved for" #3 — series colour set here, in the series
            // config, never inside getChartTheme()'s returned fragment.
            color: accentColor(),
        }],
        credits: { enabled: false },
    }));
}

async function fetchChatHours(): Promise<void> {
    const seq = ++chatHoursSeq;
    const url = `?do=getChatHours${serverQueryTerms()}`;

    try {
        const resp = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!resp.ok) throw new Error(String(resp.status));

        const payload: ChatHoursPayload = await resp.json();
        if (seq !== chatHoursSeq) return; // a newer request has already been issued
        renderChatHours(payload);
    } catch (err) {
        console.error('[mc-feed] Chat hours fetch error:', err);
        if (seq === chatHoursSeq) renderChatChartError('mc-feed-chat-hours', () => { hoursChart = null; });
    }
}

function renderChatHours(payload: ChatHoursPayload): void {
    const container = document.getElementById('mc-feed-chat-hours');
    if (!container) return;

    if (hoursChart) {
        hoursChart.destroy();
        hoursChart = null;
    }

    if (!payload.has_data) {
        container.classList.add('chart-empty');
        container.replaceChildren(...emptyStateNodes(
            'No chat activity yet',
            'No chat messages have been recorded yet. Message content is never shown here — only counts.',
        ));
        return;
    }

    if (!seriesTypeAvailable('column')) {
        console.error('[mc-feed] Highcharts column series unavailable — skipping the chat hours chart.');
        return;
    }

    container.classList.remove('chart-empty');
    container.replaceChildren();

    hoursChart = Highcharts.chart(container, Highcharts.merge(getChartTheme(), {
        chart: { type: 'column', backgroundColor: 'transparent' },
        title: { text: '' },
        // Hours are already Toronto-local (server-side shift, McFeedModel::getChatHours()).
        xAxis: { categories: payload.bars.map((b) => String(b.hour)), title: { text: 'Hour (local)' } },
        yAxis: { min: 0, allowDecimals: false, title: { text: 'Messages' } },
        legend: { enabled: false },
        series: [{
            type: 'column',
            name: 'Messages',
            data: payload.bars.map((b) => b.count),
            color: accentColor(),
        }],
        credits: { enabled: false },
    }));
}

function renderChatChartError(containerId: string, resetChart: () => void): void {
    const container = document.getElementById(containerId);
    if (!container) return;

    resetChart();
    container.classList.add('chart-empty');
    container.replaceChildren(...emptyStateNodes(
        "Couldn't load this chart",
        'The chart could not be refreshed just now. Reload the page to try again.',
    ));
}

/** CHRT-03 / §7.6 — resolved live via getComputedStyle, mirroring chartTheme.ts's own token()
 *  helper (not exported, so re-implemented locally). Never a hardcoded hex except as the WR-01
 *  same-pattern fallback for an unresolved custom property. */
function accentColor(): string {
    const value = getComputedStyle(document.documentElement).getPropertyValue('--ksat-accent').trim();
    return value || '#D89702';
}

/** Loud-guard idiom (index.ts's drilldown precedent) — a required series type missing means the
 *  bundle is broken, not that the data is empty; console.error and skip rather than throw. */
function seriesTypeAvailable(type: string): boolean {
    return typeof (Highcharts as unknown as { seriesTypes?: Record<string, unknown> }).seriesTypes?.[type] !== 'undefined';
}

// ---------------------------------------------------------------------------
// Word Count Ranking (§7.6.3, Pitfall 6 — client-side DataTable, no remote data source)
// ---------------------------------------------------------------------------

async function fetchChatWordRank(): Promise<void> {
    const seq = ++wordRankSeq;
    const url = `?do=getChatWordRank${serverQueryTerms()}`;

    try {
        const resp = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!resp.ok) throw new Error(String(resp.status));

        const payload: WordRankPayload = await resp.json();
        if (seq !== wordRankSeq) return; // a newer request has already been issued
        renderChatWordRank(payload);
    } catch (err) {
        console.error('[mc-feed] Word rank fetch error:', err);
        if (seq === wordRankSeq) renderChatWordRankError();
    }
}

/** The table element is NEVER removed from the DOM (unlike the empty-card swap used elsewhere) —
 *  a server-picker change must be able to find it again on the next refetch. A sibling empty/error
 *  card is toggled instead, mirroring analytics.ts's own chart/empty pair idiom. */
function wordRankEmptyEl(wrapper: HTMLElement): HTMLElement {
    let empty = document.getElementById('mc-feed-word-rank-empty');
    if (!empty) {
        empty = document.createElement('div');
        empty.id = 'mc-feed-word-rank-empty';
        empty.className = 'd-none';
        wrapper.insertAdjacentElement('afterend', empty);
    }
    return empty;
}

function renderChatWordRank(payload: WordRankPayload): void {
    const tableEl = document.querySelector<HTMLTableElement>('table[data-table="mc-feed-word-rank"]');
    if (!tableEl) return;
    const wrapper = tableEl.closest<HTMLElement>('.table-responsive');
    if (!wrapper) return;
    const emptyEl = wordRankEmptyEl(wrapper);

    // Destroy any existing client-side instance before mutating the tbody — DataTable takes
    // ownership of the table's DOM on init (Pitfall 6).
    if (wordRankTable) {
        wordRankTable.destroy();
        wordRankTable = null;
    }

    const tbody = tableEl.querySelector('tbody');
    if (tbody) tbody.replaceChildren();

    if (!payload.has_data || payload.rows.length === 0) {
        wrapper.classList.add('d-none');
        emptyEl.replaceChildren(emptyCard(
            'No chat activity yet',
            'No chat messages have been recorded yet. Message content is never shown here — only counts.',
        ));
        emptyEl.classList.remove('d-none');
        return;
    }

    wrapper.classList.remove('d-none');
    emptyEl.classList.add('d-none');

    if (tbody) {
        for (const row of payload.rows) {
            tbody.appendChild(wordRankRowEl(row));
        }
    }

    // Client-side DataTable (Pitfall 6) — one small payload; sort/search added on top of the
    // already-populated rows. No remote data source of any kind is configured below.
    wordRankTable = new DataTable(tableEl, {
        pageLength: 25,
        order: [[2, 'desc']],
    });
}

function renderChatWordRankError(): void {
    const tableEl = document.querySelector<HTMLTableElement>('table[data-table="mc-feed-word-rank"]');
    if (!tableEl) return;
    const wrapper = tableEl.closest<HTMLElement>('.table-responsive');
    if (!wrapper) return;
    const emptyEl = wordRankEmptyEl(wrapper);

    if (wordRankTable) {
        wordRankTable.destroy();
        wordRankTable = null;
    }

    wrapper.classList.add('d-none');
    emptyEl.replaceChildren(emptyCard(
        "Couldn't load this table",
        'The table could not be refreshed just now. Reload the page to try again.',
    ));
    emptyEl.classList.remove('d-none');
}

function wordRankRowEl(row: WordRankRow): HTMLElement {
    const tr = document.createElement('tr');
    tr.dataset.uuid = row.player_uuid;

    const playerTd = document.createElement('td');
    playerTd.appendChild(playerLinkEl(row.player_uuid, row.player_name));
    tr.appendChild(playerTd);

    const messagesTd = document.createElement('td');
    messagesTd.className = 'mc-board-value';
    messagesTd.textContent = formatCount(row.messages);
    tr.appendChild(messagesTd);

    const wordsTd = document.createElement('td');
    wordsTd.className = 'mc-board-value';
    wordsTd.textContent = formatCount(row.total_words);
    tr.appendChild(wordsTd);

    const avgTd = document.createElement('td');
    avgTd.className = 'mc-board-value';
    avgTd.textContent = row.avg_words.toLocaleString('en-US', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
    });
    tr.appendChild(avgTd);

    return tr;
}

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

/** The locked house error-state pattern (§ Copywriting Contract), reused by every surface. */
function emptyCard(title: string, body: string): HTMLElement {
    const card = document.createElement('div');
    card.className = 'chart chart-empty';
    const [heading, state] = emptyStateNodes(title, body);
    card.appendChild(heading);
    card.appendChild(state);
    return card;
}

/** The bare title+body pair, for containers that already carry the `.chart.chart-empty` classes
 *  on themselves (the two Chat Activity chart containers — feed.latte renders them that way). */
function emptyStateNodes(title: string, body: string): [HTMLElement, HTMLElement] {
    const heading = document.createElement('div');
    heading.className = 'chart-empty-title';
    heading.textContent = title;

    const state = document.createElement('div');
    state.className = 'ksat-empty-state';
    const p = document.createElement('p');
    p.className = 'text-muted mb-0';
    p.textContent = body;
    state.appendChild(p);

    return [heading, state];
}

/** D-13-style rank medal, IN ADDITION to the number — mirrors leaderboard.ts's appendRankCell(). */
function appendRankCell(td: HTMLElement, rank: number): void {
    const num = document.createElement('span');
    num.className = 'mc-board-rank-num';
    num.textContent = String(rank);
    td.appendChild(num);

    if (rank <= 3) {
        const cls = rank === 1 ? 'gold' : rank === 2 ? 'silver' : 'bronze';
        const label = rank === 1 ? 'First place' : rank === 2 ? 'Second place' : 'Third place';
        const medal = document.createElement('span');
        medal.className = `mc-rank-medal mc-rank-medal--${cls}`;
        medal.title = label;
        medal.setAttribute('aria-label', label);
        td.appendChild(document.createTextNode(' '));
        td.appendChild(medal);
    }
}

/** Avatar tile + profile link — mirrors leaderboard.ts's playerCell() (avatar-only subset, this
 *  page has no unresolved/non-Mojang badges to render). */
function playerLinkEl(uuid: string, name: string): HTMLElement {
    const wrap = document.createElement('span');
    wrap.className = 'mc-board-player';

    const tile = document.createElement('span');
    tile.className = 'mc-avatar-tile';
    const img = document.createElement('img');
    img.src = `https://mc-heads.net/avatar/${encodeURIComponent(uuid)}/32`;
    img.loading = 'lazy';
    img.width = 32;
    img.height = 32;
    img.alt = '';
    img.className = 'mc-player-avatar';
    img.addEventListener('error', () => { img.style.visibility = 'hidden'; });
    tile.appendChild(img);
    wrap.appendChild(tile);

    const link = document.createElement('a');
    link.href = `/mc/players/${encodeURIComponent(uuid)}`;
    link.title = 'View profile';
    const nameEl = document.createElement('span');
    nameEl.className = 'mc-board-name';
    nameEl.textContent = name;
    link.appendChild(nameEl);
    wrap.appendChild(link);

    return wrap;
}

function th(text: string, className?: string): HTMLTableCellElement {
    const el = document.createElement('th');
    el.scope = 'col';
    if (className) el.className = className;
    el.textContent = text;
    return el;
}

function formatCount(value: number): string {
    return value.toLocaleString('en-US', { maximumFractionDigits: 0 });
}
